import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Users, Gift as GiftIcon, Plus } from "lucide-react";
import GiftModal from "@/components/modals/GiftModal";
import { useAuth } from "@/context/AuthContext";

export default function Circle() {
  const { user } = useAuth();
  const [circles, setCircles] = useState([]);
  const [name, setName] = useState("");
  const [phones, setPhones] = useState("");
  const [openGift, setOpenGift] = useState(false);
  const [balance, setBalance] = useState(0);

  const load = async () => {
    const [c, s] = await Promise.all([api.get("/circle/list"), api.get("/wallet/summary")]);
    setCircles(c.data.circles || []);
    setBalance(s.data.grams || 0);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) return toast.error("Please name your circle");
    try {
      await api.post("/circle", {
        name: name.trim(),
        members: phones.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Circle created");
      setName(""); setPhones("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="pt-6">
      <div className="text-xs font-bold tracking-[0.22em] uppercase text-brand-primary">Circle</div>
      <h1 className="mt-1 font-display text-4xl font-semibold text-brand-charcoal">Save & gift as a family</h1>
      <p className="mt-2 text-brand-slate">Rotate contributions in a modern-day kitty. Gift gold instantly.</p>

      <div className="mt-6 rounded-3xl overflow-hidden border border-brand-line bg-brand-sand shadow-soft">
        <div className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-display text-2xl text-brand-charcoal">Send a gift</div>
              <div className="text-sm text-brand-slate">Perfect for weddings, birthdays, festivals.</div>
            </div>
            <GiftIcon className="text-brand-primary" />
          </div>
          <Button
            data-testid="circle-gift-btn"
            onClick={() => setOpenGift(true)}
            className="mt-4 w-full h-12 rounded-full bg-brand-primary hover:bg-brand-primary-soft text-brand-ivory font-semibold shadow-card"
          >
            Gift gold now
          </Button>
        </div>
      </div>

      <div className="mt-6 rounded-3xl border border-brand-line bg-brand-ivory p-5 shadow-soft space-y-3">
        <h3 className="font-display text-2xl text-brand-charcoal">Create a Circle</h3>
        <div>
          <Label className="text-brand-charcoal text-sm font-semibold">Circle name</Label>
          <Input data-testid="circle-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sharma family" className="mt-2 h-12 rounded-xl border-brand-line" />
        </div>
        <div>
          <Label className="text-brand-charcoal text-sm font-semibold">Member phone numbers (comma separated)</Label>
          <Input data-testid="circle-members" value={phones} onChange={(e) => setPhones(e.target.value)} placeholder="9876543210, 9812345678" className="mt-2 h-12 rounded-xl border-brand-line" />
        </div>
        <Button data-testid="circle-create" onClick={create} className="w-full h-12 rounded-full bg-gold-gradient text-brand-page hover:opacity-90">
          <Plus size={16} className="mr-1" /> Create Circle
        </Button>
      </div>

      <h2 className="mt-8 font-display text-2xl text-brand-charcoal">Your circles</h2>
      <div data-testid="circle-list" className="mt-3 space-y-3">
        {circles.length === 0 && <div className="text-brand-slate text-sm">No circles yet.</div>}
        {circles.map((c) => (
          <div key={c.id} className="rounded-2xl border border-brand-line bg-brand-ivory p-4 flex items-center gap-3 shadow-soft">
            <div className="h-11 w-11 rounded-full bg-brand-primary text-brand-ivory flex items-center justify-center">
              <Users size={18} />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-brand-charcoal">{c.name}</div>
              <div className="text-xs text-brand-slate">{(c.members || []).length} member(s)</div>
            </div>
            <div className="text-sm tabular-nums font-semibold text-brand-primary">{Number(c.total_grams || 0).toFixed(4)}g</div>
          </div>
        ))}
      </div>

      <GiftModal open={openGift} onOpenChange={setOpenGift} onDone={load} balance={balance} />
    </div>
  );
}
